/**
 * The report being edited.
 *
 * Holds the document, what is selected, and the history behind it. Every edit
 * goes through here, which is what makes undo possible at all: an edit made
 * directly against a section would leave no record of what the document looked
 * like before.
 *
 * **Every operation replaces the tree rather than mutating it.** The previous
 * tree then stays valid, so undo is keeping a reference rather than replaying
 * inverse operations, and there is no operation whose inverse somebody forgot
 * to write.
 *
 * Saving is on a timer. Nobody should have to remember to save a document they
 * are in the middle of writing, and nobody should lose an afternoon because
 * they did not.
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'

import { ApiError, NetworkError } from '@/api/http'
import { principalOf, type DocumentReporting } from '@/domain/csrdReportings'
import {
  contentsOf,
  numberingOf,
  readDocument,
  writeDocument,
  type ContentsEntry,
} from '@/domain/report'
import {
  createSection,
  duplicateSection,
  isGroup,
  type Section,
  type SectionKind,
} from '@/domain/section'
import * as tree from '@/domain/tree'
import { closedWithin, effectiveDirections, mayWrite, type Writer } from '@/domain/direction'
import { mergeImportedFonts, type ImportedFont } from '@/domain/facsimile/fonts'
import { withHouseStyle, type HouseStyle } from '@/domain/houseStyle'
import { DEFAULT_LAYOUT, layoutStyle, type Layout } from '@/domain/layout'
import { DEFAULT_TYPEFACE, typefaceOf, typefacesInUse, type TypefaceId } from '@/domain/typeface'
import { ensureHeadingFace, ensureTypeface } from '@/features/typography/load'
import { t } from '@/i18n'
import { useAccessStore } from '@/stores/access'
import { useDraftsStore } from '@/stores/drafts'

/** How long after the last keystroke to save. */
const SAVE_AFTER_MS = 1200

/** How many steps back it is possible to go. */
const HISTORY_LIMIT = 100

/** Where a dragged section would land. */
export interface DropTarget {
  readonly parentId: string | null
  readonly index: number
}

export const useReportStore = defineStore('report', () => {
  const drafts = useDraftsStore()
  const access = useAccessStore()

  /**
   * The document.
   *
   * `shallowRef`: the tree is replaced wholesale on every edit, so making
   * every nested section individually reactive would cost a deep proxy walk
   * per keystroke and buy nothing.
   */
  const sections = shallowRef<readonly Section[]>([])
  const origin = ref('')
  const typeface = ref<TypefaceId>(DEFAULT_TYPEFACE)
  const layout = ref<Layout>(DEFAULT_LAYOUT)
  /** The fonts imported pages are drawn in. See `domain/facsimile/fonts.ts`. */
  const fonts = shallowRef<readonly ImportedFont[]>([])
  /** The CSRD reportings the report is tied to. See `domain/csrdReportings.ts`. */
  const reportings = shallowRef<readonly DocumentReporting[]>([])
  /** The one the report is about, and the one CSRD is kept on. */
  const principal = computed(() => principalOf(reportings.value))

  const selectedId = ref<string | null>(null)
  /**
   * The last section actually selected, kept even once `selectedId` is
   * cleared back to `null`.
   *
   * Closing the properties panel is what brings the palette back — the rail
   * shows one or the other, never both — and that close sends `selectedId` to
   * `null`. `add()` inserts next to this instead, so the ordinary path of
   * "select something, close it, add next to it" still lands where it was
   * asked to rather than always at the end of the document.
   */
  const anchorId = ref<string | null>(null)
  /**
   * Everything selected, in the order it was picked. `selectedId` stays the one
   * the inspector is about (the last picked); this is the run that copying,
   * moving and deleting act on. Always holds `selectedId` when there is one.
   */
  const selection = ref<readonly string[]>([])
  /** Where a range picked with Shift starts: the last section picked on its own. */
  const rangeAnchor = ref<string | null>(null)
  const dirty = ref(false)
  const saveProblem = ref<string | null>(null)

  /**
   * The section a drop just put somewhere, for a moment.
   *
   * A drop into a report of forty sections is otherwise an act of faith: the
   * page scrolls, something lands, and nothing says which thing moved. This is
   * cleared on a timer rather than on the next edit, because it is an
   * acknowledgement and not a state.
   */
  const justPlacedId = ref<string | null>(null)
  let placedTimer: ReturnType<typeof setTimeout> | null = null

  function markPlaced(id: string): void {
    justPlacedId.value = id
    if (placedTimer !== null) clearTimeout(placedTimer)
    placedTimer = setTimeout(() => {
      justPlacedId.value = null
      placedTimer = null
    }, 900)
  }

  const past = shallowRef<readonly (readonly Section[])[]>([])
  const future = shallowRef<readonly (readonly Section[])[]>([])

  let saveTimer: ReturnType<typeof setTimeout> | null = null

  const selected = computed<Section | null>(() =>
    selectedId.value ? tree.find(sections.value, selectedId.value) : null,
  )

  /** The outline: everything visible, with its depth. */
  const outline = computed(() => tree.flatten(sections.value))

  /**
   * The sections picked, outermost only and in the order of the document: a
   * group picked with what is inside it counts once, as the group.
   */
  const selectedRoots = computed(() => tree.selectionRoots(sections.value, selection.value))
  /** How many sections the actions on a selection would reach. */
  const selectedCount = computed(() => selectedRoots.value.length)
  const picked = computed(() => new Set(selection.value))

  function isSelected(id: string): boolean {
    return picked.value.has(id)
  }

  const sectionCount = computed(() => tree.count(sections.value))

  const canUndo = computed(() => past.value.length > 0)
  const canRedo = computed(() => future.value.length > 0)

  /** Whether the document may be changed at all, which the server decides. */
  const editable = computed(() => drafts.canEdit)

  // ── Who may write where ──────────────────────────────────────────────
  //
  // A section given to a direction is closed to anyone outside it. What is
  // decided here is only what to offer: the server refuses the rest.

  /** Each section's directions once its chapter's are passed down. */
  const directionsById = computed(() => effectiveDirections(sections.value))

  const writer = computed<Writer>(() => ({
    directions: new Set(access.me?.directions ?? []),
    writesEverywhere: access.capabilities.writesEverywhere,
  }))

  /** Whether this one section may be changed by this person, in this state of the report. */
  function canEditSection(id: string): boolean {
    // A shell stands for a section this person may not even read, let alone change.
    if (tree.find(sections.value, id)?.masked) return false
    return editable.value && mayWrite(directionsById.value.get(id) ?? [], writer.value)
  }

  /** Whether this section and everything inside it may be removed or moved. */
  function canChangeSection(id: string): boolean {
    const section = tree.find(sections.value, id)
    return (
      editable.value &&
      section !== null &&
      !closedWithin(section, directionsById.value, writer.value)
    )
  }

  /** Whether a whole selection may be removed, copied or moved. */
  function canChangeSelection(): boolean {
    return editable.value && selectedRoots.value.every((one) => canChangeSection(one.id))
  }

  /** Who may give sections to directions, or take them back: the owner, and an administrator. */
  const mayAssignDirections = computed(
    () =>
      editable.value && (access.capabilities.writesEverywhere || drafts.open?.myRole === 'owner'),
  )

  /**
   * Every face the document is drawn in (its text, its headings, its text
   * styles) fetched as it comes into use, in the background: the page renders
   * in the fallback until each arrives, which is the right trade against a
   * blank page. A library family is fetched from our API like any other.
   */
  watch(
    () => typefacesInUse(typeface.value, layout.value.headingTypeface, layout.value.textStyles),
    (faces) => faces.forEach((face) => void ensureTypeface(face)),
    { immediate: true },
  )

  /** The font stack the page is set in, for the canvas and the export. */
  const typefaceStack = computed(() => typefaceOf(typeface.value).stack)

  /**
   * Everything the page needs to draw itself, as custom properties.
   *
   * One object rather than a rule per setting, so the canvas, the preview and
   * the printed page are given exactly the same measurements and cannot drift
   * apart.
   */
  const pageStyle = computed<Record<string, string>>(() => ({
    ...layoutStyle(layout.value),
    '--font-document': typefaceStack.value,
  }))

  /** The contents page, worked out fresh each time. */
  function contents(depth: number): ContentsEntry[] {
    return contentsOf(sections.value, depth)
  }

  /**
   * Every heading's number, by section id.
   *
   * Worked out once for the whole document rather than by each heading for
   * itself: a heading's number depends on everything before it, and a block
   * knows only about itself. Empty when the document is not numbered, which
   * costs nothing to look up.
   */
  const headingNumbers = computed(() => numberingOf(sections.value, layout.value.headingNumbers))

  /**
   * Set the report in a different typeface.
   *
   * The font is fetched before the change is recorded, so the page does not
   * flash through the fallback on the way. Not awaited by the caller: the
   * fallback renders immediately and the real face swaps in when it arrives.
   */
  async function setTypeface(id: TypefaceId): Promise<void> {
    if (!editable.value || id === typeface.value) return

    await ensureTypeface(id)
    typeface.value = id
    dirty.value = true
    scheduleSave()
  }

  /**
   * Change part of the layout.
   *
   * Not an undo step. Nobody presses undo expecting the margins to change
   * back, and mixing a layout change into the history of a document's content
   * makes both harder to reason about.
   */
  function setLayout(changes: Partial<Layout>): void {
    if (!editable.value) return
    layout.value = { ...layout.value, ...changes }
    dirty.value = true
    scheduleSave()
  }

  /**
   * Put a house style on the report: its typeface and its look, in one change
   * and one save. The faces are fetched first, as for `setTypeface`, so the
   * page does not flash through a fallback on the way.
   */
  async function applyHouseStyle(style: HouseStyle): Promise<void> {
    if (!editable.value) return
    await Promise.all([
      ensureTypeface(style.typeface),
      ensureHeadingFace(style.look.headingTypeface),
    ])
    typeface.value = style.typeface
    layout.value = withHouseStyle(layout.value, style)
    dirty.value = true
    scheduleSave()
  }

  /**
   * Set the reportings the report is tied to.
   *
   * Not an undo step, for the same reason as the layout: it is a setting of the
   * report, not something written in it.
   */
  function setReportings(next: readonly DocumentReporting[]): void {
    if (!editable.value) return
    reportings.value = next
    dirty.value = true
    scheduleSave()
  }

  /**
   * Take in the fonts an import brought.
   *
   * A font the document already holds keeps what somebody decided about it
   * (an uploaded file, a chosen substitute) and only gains the characters the
   * new pages printed. Not an undo step, for the same reason as the layout.
   */
  function mergeFonts(incoming: readonly ImportedFont[]): void {
    if (!editable.value || incoming.length === 0) return
    fonts.value = mergeImportedFonts(fonts.value, incoming)
    dirty.value = true
    scheduleSave()
  }

  /** Change one font: an uploaded file, another substitute. */
  function setFont(key: string, changes: Partial<Omit<ImportedFont, 'key'>>): void {
    if (!editable.value) return
    fonts.value = fonts.value.map((font) => (font.key === key ? { ...font, ...changes } : font))
    dirty.value = true
    scheduleSave()
  }

  // ── Loading and saving ─────────────────────────────────────────────────

  /** Take the document from the draft the drafts store has open. */
  function adopt(): void {
    const draft = drafts.open
    const document = readDocument(draft?.payload ?? null)

    sections.value = document.sections
    origin.value = document.origin
    typeface.value = document.typeface
    layout.value = document.layout
    fonts.value = document.fonts
    reportings.value = document.reportings
    past.value = []
    future.value = []
    dirty.value = false
    saveProblem.value = null
    setSelected(document.sections[0]?.id ?? null)
  }

  /**
   * Replace the document with somebody else's version.
   *
   * Used when the live channel says the report was saved elsewhere and the
   * person here chose to take it. Clears history, because the steps behind
   * this document describe a document that no longer exists and undoing into
   * them would silently reinstate work somebody else replaced.
   */
  function adoptRemote(): void {
    adopt()
  }

  /** Record a change and start the clock on saving it. */
  function commit(next: readonly Section[]): void {
    past.value = [...past.value, sections.value].slice(-HISTORY_LIMIT)
    future.value = []
    sections.value = next
    dirty.value = true
    scheduleSave()
  }

  function scheduleSave(): void {
    if (saveTimer !== null) clearTimeout(saveTimer)
    saveTimer = setTimeout(() => {
      saveTimer = null
      void save()
    }, SAVE_AFTER_MS)
  }

  /**
   * Save now.
   *
   * Returns whether it worked. A failed autosave has to be visible: silently
   * dropping it is how an afternoon's work disappears without anybody being
   * told.
   */
  async function save(): Promise<boolean> {
    if (saveTimer !== null) {
      clearTimeout(saveTimer)
      saveTimer = null
    }
    if (!drafts.open) return false

    const worked = await drafts.save({
      payload: writeDocument({
        sections: sections.value,
        origin: origin.value,
        typeface: typeface.value,
        layout: layout.value,
        fonts: fonts.value,
        reportings: reportings.value,
      }),
    })

    if (worked) {
      dirty.value = false
      saveProblem.value = null
    } else {
      saveProblem.value = drafts.problem ?? t('common.saveFailed')
    }
    return worked
  }

  /** Save anything outstanding, for leaving the page. */
  async function flush(): Promise<void> {
    if (dirty.value) await save()
  }

  // ── History ────────────────────────────────────────────────────────────

  function undo(): void {
    const previous = past.value.at(-1)
    if (!previous) return

    future.value = [sections.value, ...future.value]
    past.value = past.value.slice(0, -1)
    sections.value = previous
    dirty.value = true
    scheduleSave()
  }

  function redo(): void {
    const next = future.value[0]
    if (!next) return

    past.value = [...past.value, sections.value]
    future.value = future.value.slice(1)
    sections.value = next
    dirty.value = true
    scheduleSave()
  }

  // ── Editing ────────────────────────────────────────────────────────────

  /**
   * Set what is selected, and — unless this is a clear — what `add()` will
   * insert next to.
   *
   * The one place either ever changes, so the two cannot drift apart: a call
   * site that meant to update where the next section lands cannot forget to,
   * and a bare `selectedId.value = …` cannot creep back in.
   */
  function setSelected(id: string | null): void {
    selectedId.value = id
    selection.value = id === null ? [] : [id]
    if (id !== null) {
      anchorId.value = id
      rangeAnchor.value = id
    }
  }

  function select(id: string | null): void {
    setSelected(id)
  }

  /** Add a section to the selection, or take it out of it (Ctrl or Cmd and a click). */
  function toggleSelected(id: string): void {
    const has = selection.value.includes(id)
    selection.value = has ? selection.value.filter((one) => one !== id) : [...selection.value, id]
    rangeAnchor.value = id
    selectedId.value = has ? (selection.value.at(-1) ?? null) : id
    if (!has) anchorId.value = id
  }

  /**
   * Select every row between the last one picked on its own and this one
   * (Shift and a click). Rows hidden inside a closed group are not between.
   */
  function selectRange(id: string): void {
    const rows = outline.value.map((row) => row.section.id)
    const from = rangeAnchor.value ?? selectedId.value
    const start = from === null ? -1 : rows.indexOf(from)
    const end = rows.indexOf(id)
    if (start === -1 || end === -1) {
      setSelected(id)
      return
    }

    selection.value = rows.slice(Math.min(start, end), Math.max(start, end) + 1)
    selectedId.value = id
    anchorId.value = id
  }

  /**
   * Add a section, next to what is selected.
   *
   * Next to, rather than at the end: somebody who has just clicked a paragraph
   * in the middle of a report and asked for a table means there, and having it
   * appear forty rows below is a small betrayal each time.
   */
  function add(kind: SectionKind, title?: string): Section | null {
    if (!editable.value) return null

    // Named in the language the person is working in, because the title is
    // written into the document and printed on the page.
    const section = createSection(kind, title ?? t(`newSection.${kind}`))
    const anchor = anchorId.value

    if (anchor) {
      const holder = tree.find(sections.value, anchor)
      // A group that is open takes the new section as its first child, which
      // is what clicking a chapter and adding to it should mean.
      if (holder && isGroup(holder) && !holder.collapsed) {
        if (!canEditSection(holder.id)) return null
        commit(tree.insert(sections.value, section, { parentId: anchor, index: 0 }))
      } else {
        commit(tree.insertAfter(sections.value, anchor, section))
      }
    } else {
      commit([...sections.value, section])
    }

    setSelected(section.id)
    return section
  }

  /**
   * Add a section at a given place, rather than next to the selection.
   *
   * What dropping one out of the palette means: the position was the whole
   * point of the gesture, so it is obeyed exactly rather than interpreted.
   */
  function addAt(kind: SectionKind, at: DropTarget, title?: string): Section | null {
    if (!editable.value) return null
    if (at.parentId !== null && !canEditSection(at.parentId)) return null

    const section = createSection(kind, title ?? t(`newSection.${kind}`))
    commit(tree.insert(sections.value, section, { parentId: at.parentId, index: at.index }))
    setSelected(section.id)
    markPlaced(section.id)
    return section
  }

  /** Add several at once, keeping their order. What an import produces. */
  function addAll(incoming: readonly Section[]): void {
    if (!editable.value || incoming.length === 0) return

    let next = [...sections.value]
    let anchor = anchorId.value

    for (const section of incoming) {
      next = anchor ? tree.insertAfter(next, anchor, section) : [...next, section]
      anchor = section.id
    }

    commit(next)
    setSelected(incoming.at(-1)?.id ?? selectedId.value)
  }

  function update(section: Section): void {
    if (!canEditSection(section.id)) return
    commit(tree.replace(sections.value, section))
  }

  /**
   * Change one part of a section.
   *
   * Typed so that the changes have to belong to that section's own kind: a
   * `level` cannot be set on a table, and a mistyped field name is a compile
   * error rather than a property nothing reads.
   */
  function patch<T extends Section>(section: T, changes: Partial<Omit<T, 'id' | 'kind'>>): void {
    update({ ...section, ...changes })
  }

  /**
   * Give a section to directions, or take it back: none means open.
   *
   * Not an `update`: the owner may give a section to a direction they are not
   * in, which is exactly what handing a chapter to Finance is, and that must not
   * need the right to write in it.
   */
  function setDirections(id: string, directions: readonly string[]): void {
    if (!mayAssignDirections.value) return
    const section = tree.find(sections.value, id)
    if (!section) return

    // Removed rather than left empty, so a section given to nobody saves as one
    // with no field.
    if (directions.length === 0) {
      const { directions: _dropped, ...rest } = section
      commit(tree.replace(sections.value, rest))
      return
    }
    commit(tree.replace(sections.value, { ...section, directions: [...directions] }))
  }

  function remove(id: string): void {
    if (!canChangeSection(id)) return

    // Select the next thing along, so the inspector is not left blank and the
    // person keeps their place in the document.
    const rows = outline.value
    const at = rows.findIndex((row) => row.section.id === id)
    const nextSelected = rows[at + 1]?.section.id ?? rows[at - 1]?.section.id ?? null

    commit(tree.remove(sections.value, id))
    if (selectedId.value === id) setSelected(nextSelected)
  }

  function duplicate(id: string): void {
    if (!canChangeSection(id)) return

    const section = tree.find(sections.value, id)
    if (!section) return

    const copy = duplicateSection(section)
    commit(tree.insertAfter(sections.value, id, copy))
    setSelected(copy.id)
  }

  /**
   * Put a block back as an earlier version of it said it (from its history, or
   * from a version the report is compared with), as one step that can be undone.
   *
   * A block still in the report is replaced where it stands: a group keeps what
   * it holds now and whether it is open, since an earlier version of the group
   * says nothing of either. A block that is gone comes back at the end of the
   * document, under its old id, and the person is taken to it.
   */
  function restoreSection(earlier: Section): 'replaced' | 'returned' | null {
    if (!editable.value) return null
    if (tree.find(sections.value, earlier.id) && !canChangeSection(earlier.id)) return null

    const now = tree.find(sections.value, earlier.id)
    if (now) {
      const kept = isGroup(now) && isGroup(earlier)
      const restored = kept
        ? { ...earlier, children: now.children, collapsed: now.collapsed }
        : earlier
      commit(tree.replace(sections.value, restored))
      return 'replaced'
    }

    commit(tree.insert(sections.value, earlier, { parentId: null, index: sections.value.length }))
    setSelected(earlier.id)
    return 'returned'
  }

  /** Move a section. Refused moves leave the document alone. */
  function move(id: string, to: DropTarget): boolean {
    if (!canChangeSection(id)) return false
    if (to.parentId !== null && !canEditSection(to.parentId)) return false
    if (tree.refuseMove(sections.value, id, to) !== null) return false

    commit(tree.move(sections.value, id, to))
    markPlaced(id)
    return true
  }

  /** Move a section one place within its own parent. */
  function nudge(id: string, direction: -1 | 1): void {
    if (!canChangeSection(id)) return
    commit(tree.nudge(sections.value, id, direction))
  }

  // ── The selection as a whole ─────────────────────────────────────────
  //
  // Each of these is one step of the history, whatever the number of sections:
  // undoing a copy of a run of six removes the run, not the last of its six.

  /** Delete everything selected, and select what comes after. */
  function removeSelection(): void {
    if (!canChangeSelection()) return
    const roots = selectedRoots.value
    const first = roots[0]
    if (!first) return

    // What goes: the roots and everything inside them.
    const going = new Set(tree.walk(roots).map((section) => section.id))
    const rows = outline.value
    const at = rows.findIndex((row) => row.section.id === first.id)
    const after = rows.slice(at).find((row) => !going.has(row.section.id))
    const before = rows
      .slice(0, Math.max(at, 0))
      .reverse()
      .find((row) => !going.has(row.section.id))

    commit(tree.removeMany(sections.value, selection.value))
    setSelected(after?.section.id ?? before?.section.id ?? null)
  }

  /**
   * Copy everything selected, in the order of the document, in one piece: right
   * after the run, or at the end of the document. The copies become the selection.
   */
  function duplicateSelection(place: tree.CopyPlace = 'after'): void {
    if (!canChangeSelection()) return

    const result = tree.duplicateMany(sections.value, selection.value, place)
    const last = result.copies.at(-1)
    if (!last) return

    commit(result.sections)
    selection.value = result.copies.map((copy) => copy.id)
    selectedId.value = last.id
    anchorId.value = last.id
    rangeAnchor.value = result.copies[0]?.id ?? last.id
  }

  /** Move everything selected together to a place. Refused moves leave the document alone. */
  function moveSelection(to: DropTarget): boolean {
    if (!canChangeSelection()) return false
    if (to.parentId !== null && !canEditSection(to.parentId)) return false
    if (tree.refuseMoveMany(sections.value, selection.value, to) !== null) return false

    commit(tree.moveMany(sections.value, selection.value, to))
    const first = selectedRoots.value[0]
    if (first) markPlaced(first.id)
    return true
  }

  /** Move everything selected to the start or to the end of the document. */
  function moveSelectionTo(edge: 'start' | 'end'): boolean {
    return moveSelection({ parentId: null, index: edge === 'start' ? 0 : sections.value.length })
  }

  /** Move everything selected one place within its parent. */
  function nudgeSelection(direction: -1 | 1): void {
    if (!canChangeSelection()) return
    commit(tree.nudgeMany(sections.value, selection.value, direction))
  }

  /**
   * Open or close a group.
   *
   * Not an edit in the sense the rest of this file means: it changes what is
   * shown, not what is written. It still has to be saved, since a report that
   * reopens with every chapter expanded has lost something the author set up,
   * but it must not consume an undo step. Nobody presses undo expecting a
   * folder to reopen.
   */
  function toggleCollapsed(id: string): void {
    const section = tree.find(sections.value, id)
    if (!section || !isGroup(section)) return

    sections.value = tree.replace(sections.value, { ...section, collapsed: !section.collapsed })
    dirty.value = true
    scheduleSave()
  }

  /**
   * A failure, in words a person can act on.
   *
   * The API's own message is preferred, because it knows what went wrong.
   * Everything else falls back to the catalogue: an exception's `message` is
   * written for a developer and is always in English.
   */
  function describe(error: unknown): string {
    if (error instanceof ApiError) return error.message
    if (error instanceof NetworkError) return t('common.unreachable')
    return t('common.problem')
  }

  return {
    sections,
    origin,
    typeface,
    typefaceStack,
    setTypeface,
    layout,
    setLayout,
    applyHouseStyle,
    fonts,
    reportings,
    principal,
    setReportings,
    mergeFonts,
    setFont,
    pageStyle,
    selectedId,
    selected,
    selection,
    selectedRoots,
    selectedCount,
    isSelected,
    outline,
    sectionCount,
    dirty,
    saveProblem,
    justPlacedId,
    canUndo,
    canRedo,
    editable,
    directionsById,
    canEditSection,
    canChangeSection,
    mayAssignDirections,
    setDirections,
    contents,
    headingNumbers,
    adopt,
    adoptRemote,
    save,
    flush,
    undo,
    redo,
    select,
    toggleSelected,
    selectRange,
    removeSelection,
    duplicateSelection,
    moveSelection,
    moveSelectionTo,
    nudgeSelection,
    add,
    addAt,
    addAll,
    update,
    patch,
    remove,
    duplicate,
    restoreSection,
    move,
    nudge,
    toggleCollapsed,
    describe,
  }
})
